/**
 * Склонения подписей точек/филиалов для типовых фраз («несколько салонов», «выбор салона», «по всем салонам»).
 * Имена в API — именительный мн. / ед.; для кастомных подписей есть эвристики по окончаниям.
 */

export type BranchGender = 'm' | 'f' | 'n';

export type BranchLabelCases = {
  plural: string;
  singular: string;
  pluralLc: string;
  singularLc: string;
  /** Грамматический род: нужен для числительных («одна студия», «один зал»). */
  gender: BranchGender;
  /** Род. мн.: «несколько салонов», «нет салонов» */
  genitivePluralLc: string;
  /** Род. ед.: «выбор салона» */
  genitiveSingularLc: string;
  /** Дат. мн.: «по всем салонам», «данные по филиалам» */
  dativePluralLc: string;
};

type Triple = { genitivePlural: string; genitiveSingular: string; dativePlural: string };

const KNOWN: Record<string, Triple> = {
  'салоны|салон': { genitivePlural: 'салонов', genitiveSingular: 'салона', dativePlural: 'салонам' },
  'филиалы|филиал': { genitivePlural: 'филиалов', genitiveSingular: 'филиала', dativePlural: 'филиалам' },
  'студии|студия': { genitivePlural: 'студий', genitiveSingular: 'студии', dativePlural: 'студиям' },
  'точки|точка': { genitivePlural: 'точек', genitiveSingular: 'точки', dativePlural: 'точкам' },
  'локации|локация': { genitivePlural: 'локаций', genitiveSingular: 'локации', dativePlural: 'локациям' },
  'офисы|офис': { genitivePlural: 'офисов', genitiveSingular: 'офиса', dativePlural: 'офисам' },
  'магазины|магазин': { genitivePlural: 'магазинов', genitiveSingular: 'магазина', dativePlural: 'магазинам' },
  'центры|центр': { genitivePlural: 'центров', genitiveSingular: 'центра', dativePlural: 'центрам' },
  'клубы|клуб': { genitivePlural: 'клубов', genitiveSingular: 'клуба', dativePlural: 'клубам' },
  'залы|зал': { genitivePlural: 'залов', genitiveSingular: 'зала', dativePlural: 'залам' },
  'павильоны|павильон': { genitivePlural: 'павильонов', genitiveSingular: 'павильона', dativePlural: 'павильонам' },
  'кабинеты|кабинет': { genitivePlural: 'кабинетов', genitiveSingular: 'кабинета', dativePlural: 'кабинетам' },
  'комнаты|комната': { genitivePlural: 'комнат', genitiveSingular: 'комнаты', dativePlural: 'комнатам' },
  'цеха|цех': { genitivePlural: 'цехов', genitiveSingular: 'цеха', dativePlural: 'цехам' },
  'боксы|бокс': { genitivePlural: 'боксов', genitiveSingular: 'бокса', dativePlural: 'боксам' },
};

const KNOWN_GENDER: Record<string, BranchGender> = {
  салон: 'm',
  филиал: 'm',
  студия: 'f',
  точка: 'f',
  локация: 'f',
  офис: 'm',
  магазин: 'm',
  центр: 'm',
  клуб: 'm',
  зал: 'm',
  павильон: 'm',
  кабинет: 'm',
  комната: 'f',
  цех: 'm',
  бокс: 'm'
};

function pairKey(pluralLc: string, singularLc: string): string {
  return `${pluralLc}|${singularLc}`;
}

function knownTriple(pluralLc: string, singularLc: string): Triple | null {
  return KNOWN[pairKey(pluralLc, singularLc)] ?? null;
}

function genitiveSingularFromKnown(singularLc: string): string | null {
  for (const [key, triple] of Object.entries(KNOWN)) {
    const parts = key.split('|');
    if (parts.length === 2 && parts[1] === singularLc) {
      return triple.genitiveSingular;
    }
  }
  return null;
}

const VOWEL_END = /[аеёиоуыэюяьъ]$/;

/** Род. мн.ч. */
function genitivePluralHeuristic(pluralLc: string, singularLc: string): string {
  const hit = knownTriple(pluralLc, singularLc);
  if (hit) return hit.genitivePlural;

  if (pluralLc.endsWith('ы')) {
    // Женский род на -а: «комнаты» → «комнат», а не «комнатов».
    if (singularLc.endsWith('а')) return singularLc.slice(0, -1);
    return `${pluralLc.slice(0, -1)}ов`;
  }
  // Мужской род с необычной формой мн. ч. на -а: «цеха» → «цехов».
  if (pluralLc.endsWith('а') && !VOWEL_END.test(singularLc)) {
    return `${singularLc}ов`;
  }
  /** Им. мн. «Xи» от основы «X» (бутик → бутики): род. мн. Xов */
  if (pluralLc.endsWith('и') && pluralLc === `${singularLc}и`) {
    return `${singularLc}ов`;
  }
  if (pluralLc.endsWith('и')) {
    if (pluralLc.length >= 4 && pluralLc.endsWith('ии')) {
      return `${pluralLc.slice(0, -2)}ий`;
    }
    if (singularLc.endsWith('ка') && pluralLc.endsWith('ки')) {
      return `${pluralLc.slice(0, -2)}ек`;
    }
    if (singularLc.endsWith('ня') && pluralLc.endsWith('ни')) {
      return `${pluralLc.slice(0, -2)}ен`;
    }
    return `${pluralLc.slice(0, -1)}ей`;
  }
  return pluralLc;
}

/** Род. ед.ч. */
function genitiveSingularHeuristic(singularLc: string): string {
  const fromTable = genitiveSingularFromKnown(singularLc);
  if (fromTable) return fromTable;

  if (singularLc.endsWith('ия')) {
    return `${singularLc.slice(0, -2)}ии`;
  }
  if (singularLc.endsWith('ка')) {
    return `${singularLc.slice(0, -2)}ки`;
  }
  if (singularLc.endsWith('а') && !singularLc.endsWith('ия')) {
    return `${singularLc.slice(0, -1)}ы`;
  }
  if (!VOWEL_END.test(singularLc)) {
    return `${singularLc}а`;
  }
  return singularLc;
}

/** Дат. мн.ч. из им. мн. (для «по всем …», «данные по …»). */
function dativePluralHeuristic(pluralLc: string, singularLc: string): string {
  const hit = knownTriple(pluralLc, singularLc);
  if (hit) return hit.dativePlural;

  if (pluralLc.endsWith('ы')) {
    return `${pluralLc.slice(0, -1)}ам`;
  }
  if (pluralLc.endsWith('и') && pluralLc === `${singularLc}и`) {
    return `${singularLc}ам`;
  }
  if (pluralLc.length >= 4 && pluralLc.endsWith('ии')) {
    return `${pluralLc.slice(0, -2)}иям`;
  }
  if (singularLc.endsWith('ка') && pluralLc.endsWith('ки')) {
    return `${pluralLc.slice(0, -2)}кам`;
  }
  if (pluralLc.endsWith('и')) {
    return `${pluralLc.slice(0, -1)}ям`;
  }
  return `${pluralLc}ам`;
}

/** Род для известных подписей; для кастомных — эвристика по окончанию. */
function detectGender(singularLc: string): BranchGender {
  const known = KNOWN_GENDER[singularLc];
  if (known) return known;
  if (/[ая]$/.test(singularLc)) return 'f';
  if (/[оеё]$/.test(singularLc)) return 'n';
  if (/ь$/.test(singularLc)) return 'f';
  return 'm';
}

type PluralCategory = 'one' | 'few' | 'many';

function pluralCategory(n: number): PluralCategory {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'one';
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'few';
  return 'many';
}

const ONE_WORD: Record<BranchGender, string> = { m: 'Один', f: 'Одна', n: 'Одно' };
const TWO_WORD: Record<BranchGender, string> = { m: 'Два', f: 'Две', n: 'Два' };
const SMALL_WORDS = [
  'Ноль',
  'Один',
  'Два',
  'Три',
  'Четыре',
  'Пять',
  'Шесть',
  'Семь',
  'Восемь',
  'Девять',
  'Десять'
];

function numeralWord(n: number, category: PluralCategory, gender: BranchGender): string {
  if (category === 'one') return n <= 10 ? ONE_WORD[gender] : String(n);
  if (category === 'few') {
    if (n === 2) return TWO_WORD[gender];
    return n <= 10 ? SMALL_WORDS[n] : String(n);
  }
  return n <= 10 ? SMALL_WORDS[n] : String(n);
}

/**
 * Согласованная фраза «количество + подпись»:
 * 1 → «Одна студия», 2–4 → «Две студии», 0/5+ → «Пять студий».
 */
export function formatBranchCount(count: number, cases: BranchLabelCases): string {
  const n = Math.abs(Math.trunc(Number.isFinite(count) ? count : 0));
  const category = pluralCategory(n);
  const noun =
    category === 'one'
      ? cases.singularLc
      : category === 'few'
        ? cases.genitiveSingularLc
        : cases.genitivePluralLc;
  return `${numeralWord(n, category, cases.gender)} ${noun}`;
}

export function buildBranchLabelCases(plural: string, singular: string): BranchLabelCases {
  const pluralT = (plural || '').trim();
  const singularT = (singular || '').trim();
  const pluralLc = pluralT.toLocaleLowerCase('ru-RU');
  const singularLc = singularT.toLocaleLowerCase('ru-RU');

  return {
    plural: pluralT,
    singular: singularT,
    pluralLc,
    singularLc,
    gender: detectGender(singularLc),
    genitivePluralLc: genitivePluralHeuristic(pluralLc, singularLc),
    genitiveSingularLc: genitiveSingularHeuristic(singularLc),
    dativePluralLc: dativePluralHeuristic(pluralLc, singularLc),
  };
}
